const STORAGE_KEY = 'learnops.logout_redirect_url';
const PENDING_SESSION_KEY = 'learnops.pending_logout_redirect';

/**
 * Before sign-out when using a custom org logout URL, stash the final URL.
 * User lands on `redirectSignOut` (e.g. /login, must match PATHS.LOGIN); Login
 * consumes this and navigates (same path for SSO and password users).
 */
export const setPendingLogoutRedirect = (url: string) => {
  try {
    const value = url.trim();
    if (value) sessionStorage.setItem(PENDING_SESSION_KEY, value);
    else sessionStorage.removeItem(PENDING_SESSION_KEY);
  } catch {
    // ignore
  }
};

/** Read and clear pending URL (call once when Login loads after sign-out). */
export const takePendingLogoutRedirect = (): string | null => {
  try {
    const raw = sessionStorage.getItem(PENDING_SESSION_KEY);
    sessionStorage.removeItem(PENDING_SESSION_KEY);
    const value = String(raw ?? '').trim();
    return value.length > 0 ? value : null;
  } catch {
    return null;
  }
};

export const setLogoutRedirectUrl = (url: string | null | undefined) => {
  try {
    const value = typeof url === 'string' ? url.trim() : '';
    if (value) localStorage.setItem(STORAGE_KEY, value);
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
};

export const getLogoutRedirectUrl = (): string | null => {
  try {
    const value = String(localStorage.getItem(STORAGE_KEY) ?? '').trim();
    return value.length > 0 ? value : null;
  } catch {
    return null;
  }
};

export const clearLogoutRedirectUrl = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
};

const POST_TIMEOUT_KEY = 'learnops.post_timeout_redirect';

/**
 * Guard for values we will redirect the browser to after re-login. Accepts only
 * same-origin internal paths (open-redirect protection). `/elements/...` passes
 * so Login can forward it to the design host. Returns the trimmed path (query +
 * hash preserved) or null.
 */
export const sanitizeInternalPath = (url: unknown): string | null => {
  if (typeof url !== 'string') return null;
  const value = url.trim();
  if (!value) return null;
  if (!value.startsWith('/')) return null; // must be an absolute internal path
  if (value.startsWith('//')) return null; // protocol-relative
  if (value.includes('\\')) return null; // backslash tricks
  if (/^\/*login(\/|\?|#|$)/i.test(value)) return null; // never loop back to /login (case-insensitive)
  return value;
};

/**
 * Before a session-timeout sign-out, stash the page the user was on so Login can
 * send them back after they log in again. No-op for unsafe/invalid values.
 */
export const setPostTimeoutRedirect = (url: string) => {
  try {
    const value = sanitizeInternalPath(url);
    if (value) sessionStorage.setItem(POST_TIMEOUT_KEY, value);
  } catch {
    // ignore
  }
};

/** Read the stashed page WITHOUT clearing it (survives the ?logout=true reload). */
export const peekPostTimeoutRedirect = (): string | null => {
  try {
    return sanitizeInternalPath(sessionStorage.getItem(POST_TIMEOUT_KEY));
  } catch {
    return null;
  }
};

export const clearPostTimeoutRedirect = () => {
  try {
    sessionStorage.removeItem(POST_TIMEOUT_KEY);
  } catch {
    // ignore
  }
};
